import { gql } from '@apollo/client';
import {
  Badge,
  Box,
  chakra,
  Flex,
  Heading,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  useDisclosure,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import capitalize from 'lodash/capitalize';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { useAuthentication } from '~auth/useAuthentication';
import { BatteryBadge } from '~components/displays/BatteryBadge';
import CoupledBadge, { CoupledState } from '~components/displays/CoupledBadge';
import { usePowerToggle } from '~components/displays/DisplayDetail/power/usePowerToggle';
import { StatusCard_DisplayFragment } from '~components/displays/DisplayDetail/summary/__generated__/StatusCard.graphql';
import { DisplayRebootModal } from '~components/displays/DisplayDetail/summary/DisplayRebootModal';
import { SummaryCard } from '~components/displays/DisplayDetail/summary/SummaryCard';
import HumidityBadge from '~components/displays/HumidityBadge';
import PowerBadge from '~components/displays/PowerBadge';
import PresenceBadge from '~components/displays/PresenceBadge';
import TemperatureBadge from '~components/displays/TemperatureBadge';
import { celsiusToFahrenheit, getTemperatureStatus } from '~components/displays/utils';
import { ChevronDownIcon, OnIcon, PowerScheduleIcon, StandbyIcon } from '~components/ui/icons';
import { RelativeTime, SHORT_FORMAT_TIME } from '~components/ui/RelativeTime';
import { Humidity, Power } from '~graphql/__generated__/types';
import { AlertRebootScheduled } from './AlertRebootScheduled';

interface Props {
  display: StatusCard_DisplayFragment;
}

const getHumidity = (humidity: Humidity[]): number | null => {
  if (humidity.length === 0) {
    return null;
  }

  const availableHumidity = humidity.filter((h) => h.isSensorPresent);

  if (availableHumidity.length === 0) {
    return null;
  }
  const parsedHumidity = availableHumidity.map((h) => ({
    ...h,
    parsedValue: parseFloat(h.value),
  }));

  const validHumidity = parsedHumidity.filter(
    (h) => !isNaN(h.parsedValue) && h.parsedValue >= 20 && h.parsedValue <= 80,
  );
  if (validHumidity.length === 0) {
    const total = parsedHumidity.reduce((sum, h) => sum + h.parsedValue, 0);
    return parseFloat((total / parsedHumidity.length).toFixed(1));
  }
  const total = validHumidity.reduce((sum, h) => sum + h.parsedValue, 0);

  return parseFloat((total / validHumidity.length).toFixed(1));
};

export function StatusCard({ display }: Props) {
  const { verifyUserPermissions } = useAuth();
  const { t } = useTranslation();
  const isConnected = Boolean(display.presence?.connected);
  const displayRebootModal = useDisclosure();
  const coupledState = display?.host?.coupledState as CoupledState | undefined;
  const temperatures = display?.temperatures;
  const humidity = display?.humidity;
  const humidityValue = humidity ? getHumidity(humidity) : null;
  const temperatureThreshold = display?.temperatureThreshold;
  const { user } = useAuthentication();
  const unit = localStorage.getItem('tempUnit') || user.tempUnit || 'Celsius';
  const { isCritical, isWarning, maxValue: rawMaxValue } = getTemperatureStatus(display);
  const maxValue =
    unit === 'Fahrenheit' && temperatures && rawMaxValue !== null
      ? celsiusToFahrenheit(rawMaxValue)
      : rawMaxValue;
  const formattedMaxValue = maxValue !== null ? maxValue.toFixed(0) : null;
  const temperatureLabel = isCritical ? t('critical') : isWarning ? t('warning') : '';
  const coupledStateLabel =
    coupledState === CoupledState.COUPLED || coupledState === CoupledState.RECOUPLED
      ? t('coupled')
      : coupledState === CoupledState.DECOUPLED
      ? t('decoupled')
      : undefined;
  const rebootJob = useMemo(() => display.reboot?.latestJob, [display]);

  const batteryReported = display.battery?.reported;
  const batteryLevel = batteryReported?.level ?? 0;
  const {
    currentPower,
    confirmationNode: togglePowerConfirmationNode,
    handleChange: togglePower,
  } = usePowerToggle({ display });
  const isOn = currentPower === Power.On;

  const isDisplaySettingsUpdateDisabled = !verifyUserPermissions([
    Permission.DisplaySettingsUpdate,
  ]);
  return (
    <SummaryCard title={t('status')}>
      <SimpleGrid columns={2} spacingX="20" spacingY="6">
        <HStack spacing="6">
          <PresenceBadge display={display} noTooltip />
          <Box>
            <Heading size="md" mb={0}>
              {isConnected ? t('connected') : t('disconnected')}
            </Heading>
            <RelativeTime
              ts={display.presence?.timestamp}
              format={SHORT_FORMAT_TIME}
              render={(time) => (
                <chakra.span fontSize="sm" color="gray.500">
                  {t('lastChanged', { time: time })}
                </chakra.span>
              )}
            />
          </Box>
        </HStack>
        <HStack spacing="6">
          <PowerBadge display={display} noTooltip />
          <Box>
            <Menu>
              <MenuButton as={Heading} size="md" mb={0} cursor="pointer">
                {capitalize(currentPower)} <ChevronDownIcon />
              </MenuButton>
              <MenuList>
                <MenuItem
                  iconSpacing="5"
                  icon={
                    isOn ? (
                      <StandbyIcon color="blue.800" width="5" height="5" />
                    ) : (
                      <OnIcon color="blue.800" width="5" height="5" />
                    )
                  }
                  onClick={togglePower}
                  isDisabled={isDisplaySettingsUpdateDisabled}
                >
                  {t('switchTo')} {isOn ? t('standby') : t('on')}
                </MenuItem>

                <MenuItem
                  iconSpacing="5"
                  icon={<PowerScheduleIcon width="5" />}
                  onClick={displayRebootModal.onOpen}
                  isDisabled={
                    isDisplaySettingsUpdateDisabled ||
                    ['RebootJobPlanned', 'RebootJobInProgress'].includes(
                      rebootJob?.__typename || '',
                    )
                  }
                >
                  {t('planReboot')}
                </MenuItem>
              </MenuList>
            </Menu>
            <RelativeTime
              ts={display.power?.reportedAt}
              format={SHORT_FORMAT_TIME}
              render={(time) => (
                <chakra.span fontSize="sm" color="gray.500">
                  {t('lastChanged', { time: time })}
                </chakra.span>
              )}
            />
          </Box>
        </HStack>
        {coupledState && coupledStateLabel && (
          <HStack spacing="6">
            <CoupledBadge display={display} noTooltip />
            <Box>
              <Heading size="md" mb={0}>
                {coupledStateLabel}
              </Heading>
              <RelativeTime
                ts={display.host?.timestamp}
                format={SHORT_FORMAT_TIME}
                render={(time) => (
                  <chakra.span fontSize="sm" color="gray.500">
                    {t('lastChanged', { time: time })}
                  </chakra.span>
                )}
              />
            </Box>
          </HStack>
        )}
        {temperatures && temperatureThreshold && display.temperatureUpdatedAt && (
          <HStack spacing="6">
            <TemperatureBadge display={display} />
            <Box>
              <Flex align="center" gap={3} mb={0}>
                <Heading size="md" mb={0} mr={3}>
                  {formattedMaxValue}
                  {unit === 'Fahrenheit' ? '°F' : '°C'}
                </Heading>
                <Badge
                  textTransform="none"
                  px={5}
                  py={1}
                  borderRadius="xl"
                  bg={isCritical ? '#CF1124' : isWarning ? '#E67635' : ''}
                  color="white"
                >
                  {temperatureLabel}
                </Badge>
              </Flex>
              <RelativeTime
                ts={display.temperatureUpdatedAt}
                format={SHORT_FORMAT_TIME}
                render={(time) => (
                  <chakra.span fontSize="sm" color="gray.500">
                    {t('lastChanged', { time: time })}
                  </chakra.span>
                )}
              />
            </Box>
          </HStack>
        )}
        {batteryReported && (
          <HStack spacing="6">
            <BatteryBadge display={display} />
            <Box>
              <Flex align="center" gap={3} mb={0}>
                <Heading size="md" mb={0}>
                  {batteryReported.powerSource === 'AC' ? t('pluggedIn') : `${batteryLevel}%`}
                </Heading>
                {batteryReported.present &&
                  batteryReported.powerSource !== 'AC' &&
                  batteryLevel <= 15 && (
                    <Badge
                      textTransform="none"
                      px={5}
                      py={1}
                      ml={2}
                      borderRadius="xl"
                      bg={batteryLevel <= 5 ? '#CF1124' : '#E67635'}
                      color="white"
                    >
                      {batteryLevel <= 5 ? t('critical') : t('warning')}
                    </Badge>
                  )}
              </Flex>
              <RelativeTime
                ts={display.battery?.reportedAt}
                format={SHORT_FORMAT_TIME}
                render={(time) => (
                  <chakra.span fontSize="sm" color="gray.500">
                    {t('lastUpdated', { time })}
                  </chakra.span>
                )}
              />
            </Box>
          </HStack>
        )}
        {humidityValue !== null && humidity && (
          <HStack spacing="6">
            <HumidityBadge display={display} />
            <Box>
              <Heading size="md" mb={0}>
                {humidityValue}%
              </Heading>
              <RelativeTime
                ts={humidity[0]?.updatedAt ?? new Date().toISOString()}
                format={SHORT_FORMAT_TIME}
                render={(time) => (
                  <chakra.span fontSize="sm" color="gray.500">
                    {t('lastChanged', { time: time })}
                  </chakra.span>
                )}
              />
            </Box>
          </HStack>
        )}
      </SimpleGrid>

      <AlertRebootScheduled display={display} />

      <DisplayRebootModal
        display={display}
        isOpen={displayRebootModal.isOpen}
        onCancel={displayRebootModal.onClose}
        onSuccess={displayRebootModal.onClose}
      />

      {togglePowerConfirmationNode}
    </SummaryCard>
  );
}

StatusCard.graphql = {
  fragments: {
    StatusCard_display: gql`
      fragment StatusCard_display on Display {
        id
        presence {
          connected
          timestamp
        }
        power {
          reported
          reportedAt
          desired
        }
        ...DisplayRebootModal_display
        ...PowerBadge_display
        ...PresenceBadge_display
        ...usePowerToggle_display
        ...CoupledBadge_display
        ...TemperatureBadge_display
        ...BatteryBadge_display
        ...HumidityBadge_display
      }
    `,
  },
};
